import React, { useEffect, useState } from "react";
import { 
  IoMenu, 
  IoChevronBackOutline, 
  IoHome, 
  IoGrid, 
  IoBag, 
  IoInformationCircle, 
} from "react-icons/io5";
import logo from "../assets/jkccLogo.png";
import { LuArrowRight, LuLogOut } from "react-icons/lu";
import { useAuth } from "./AuthContext";
import { auth } from "src/config/firebaseConfig";
import ScreenLoader from "./ScreenLoader";
import ModalLogout from "./ModalLogout";

export const Topbar = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Add a loading state
  // const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { currentUser, isLoggedIn } = useAuth();

  const handleLogout = () => {
    auth.signOut();
    setDialogOpen(false);
  }

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setLoading(false); // Set loading to false once auth state is determined
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    // Render the full-screen loader while checking auth state
    return <ScreenLoader />;
  }

  return (
    <div className="relative w-full">
      {/* Topbar */}
      <div className="flex fixed w-full h-20 bg-white/95 backdrop-blur-md px-4 lg:px-12 top-0 z-50 py-4 justify-between items-center shadow-sm border-b border-gray-200">
        {/* Sidebar Button */}
        <div className="lg:hidden flex items-center">
          <button 
            onClick={toggleSidebar}
            className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors duration-200 text-gray-700"
            aria-label="Toggle menu"
          >
            <IoMenu size={24} />
          </button>
        </div>

        {/* Logo with Text */}
        <a href="/" className="hidden lg:flex gap-3 items-center text-gray-900 hover:opacity-75 transition-opacity duration-200 flex-shrink-0">
          <img src={logo} alt="JKCC Logo" width={40} height={40} className="object-contain"/>
          <div className="flex flex-col leading-tight">
            <p className="font-bold text-sm">Jasper Kissa</p>
            <p className="text-xs text-gray-600">Expert in Computers</p>
          </div>
        </a>

        {/* Route Buttons */}
        <div className="hidden lg:flex gap-8 justify-center text-sm font-medium">
          <a href="/" className="text-gray-700 hover:text-amber-600 transition-colors duration-200 relative group">
            Home
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-600 group-hover:w-full transition-all duration-300"></span>
          </a>
          <a href="/about" className="text-gray-700 hover:text-amber-600 transition-colors duration-200 relative group">
            About
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-600 group-hover:w-full transition-all duration-300"></span>
          </a>
          <a href="/products" className="text-gray-700 hover:text-amber-600 transition-colors duration-200 relative group">
            Shop
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-600 group-hover:w-full transition-all duration-300"></span>
          </a>
          <a href="/under-construction" className="text-gray-700 hover:text-amber-600 transition-colors duration-200 relative group">
            Custom PC
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-600 group-hover:w-full transition-all duration-300"></span>
          </a>
        </div>

        {/* Login Tab */}
        {!isLoggedIn && (
          <div className="flex items-center justify-end gap-3">
            <a href="/login" className="hidden lg:inline-block text-gray-700 hover:text-amber-600 transition-colors duration-200 text-sm font-medium px-4 py-2">
              Login
            </a>
            <a href="/register"
              className="flex items-center justify-center gap-2 text-white bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 rounded-lg px-5 py-2.5 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-semibold min-w-fit">
              Sign Up
              <LuArrowRight size={18} className="hidden lg:block" />
            </a>
          </div>
        )}
        {isLoggedIn && (
          <div className="flex items-center justify-end gap-3">
            <a href="/under-construction" className="hidden lg:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors duration-200 text-sm font-medium px-3 py-2 rounded-lg hover:bg-amber-50">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm">
                {currentUser?.displayName?.charAt(0) || "U"}
              </div>
              <span className="max-w-[120px] truncate">{currentUser?.displayName || "User"}</span>
            </a>
            <button
              onClick={() => setDialogOpen(true)}
              className="p-2.5 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-lg transition-colors duration-200"
              aria-label="Logout"
            >
              <LuLogOut size={20} />
            </button>
            {isDialogOpen && (
              <ModalLogout 
                onClose={() => setDialogOpen(false)}
                onConfirm={() => handleLogout()}
              />
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      {isSidebarOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
            onClick={toggleSidebar}
          />
          
          {/* Sidebar Content */}
          <div className="fixed top-20 left-0 w-[280px] h-[calc(100vh-80px)] bg-white text-gray-900 shadow-2xl z-50 lg:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto">
            <div className="h-full p-6 flex flex-col">
              <div className="flex justify-between w-full items-center mb-8">
                <div className="flex gap-3 items-center">
                  <img src={logo} alt="logo" width={36} height={36} className="object-contain"/>
                  <div className="flex flex-col leading-tight">
                    <p className="font-bold text-sm">JKCC</p>
                    <p className="text-xs text-gray-600">Computers</p>
                  </div>
                </div>
                <button 
                  onClick={toggleSidebar}
                  className="p-2 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors duration-200"
                >
                  <IoChevronBackOutline size={20} />
                </button>
              </div>

              <nav className="space-y-1 flex-grow">
                <a
                  href="/"
                  className="p-3 rounded-lg w-full flex justify-start items-center text-gray-700 hover:bg-amber-50 hover:text-amber-600 gap-3 transition-colors duration-200 font-medium"
                >
                  <IoHome size={20} />
                  <span>Home</span>
                </a>
                <a
                  href="/about"
                  className="p-3 rounded-lg w-full flex justify-start items-center text-gray-700 hover:bg-amber-50 hover:text-amber-600 gap-3 transition-colors duration-200 font-medium"
                >
                  <IoInformationCircle size={20} />
                  <span>About Us</span>
                </a>
                <a
                  href="/under-construction"
                  className="p-3 rounded-lg w-full flex justify-start items-center text-gray-700 hover:bg-amber-50 hover:text-amber-600 gap-3 transition-colors duration-200 font-medium"
                >
                  <IoGrid size={20} />
                  <span>Brands</span>
                </a>
                <a
                  href="/products"
                  className="p-3 rounded-lg w-full flex justify-start items-center text-gray-700 hover:bg-amber-50 hover:text-amber-600 gap-3 transition-colors duration-200 font-medium"
                >
                  <IoBag size={20} />
                  <span>Shop</span>
                </a>
                
                <div className="my-4 border-t border-gray-200"></div>
                
                <a
                  href="/products"
                  className="p-3 rounded-lg w-full flex justify-start items-center text-amber-600 hover:bg-amber-50 gap-3 transition-colors duration-200 font-medium"
                >
                  📦
                  <span>Browse Categories</span>
                </a>
              </nav>

              {/* Auth Section */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                {!isLoggedIn && (
                  <>
                    <a
                      href="/login"
                      className="p-3 rounded-lg w-full flex justify-center items-center border-2 border-gray-800 text-gray-800 font-semibold hover:bg-gray-800 hover:text-white transition-colors duration-200"
                    >
                      Login
                    </a>
                    <a
                      href="/register"
                      className="p-3 rounded-lg w-full flex justify-center items-center bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold hover:from-amber-700 hover:to-amber-800 transition-colors duration-200"
                    >
                      Sign Up
                    </a>
                  </>
                )}
                
                {isLoggedIn && (
                  <button
                    onClick={() => setDialogOpen(true)}
                    className="p-3 rounded-lg w-full flex justify-center items-center bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors duration-200"
                  >
                    <LuLogOut className="mr-2" size={18} />
                    Log Out
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};